
import { useState, useCallback, useEffect } from 'react'
import { useToast } from '@/hooks/use-toast'
import { moduleService } from '@/services/moduleService'
import { useModuleRealtime } from '@/hooks/useModuleRealtime'
import type { LearnModule, ModuleFormData } from '@/types/module'

export const useModuleManagement = () => {
  const [modules, setModules] = useState<LearnModule[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true)
      const data = await moduleService.fetchModules()
      setModules(data)
    } catch (error: any) {
      console.error('Error fetching modules:', error)
      toast({
        title: "Error",
        description: "Failed to load modules",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  // Set up real-time updates
  useModuleRealtime(fetchModules)

  // Initialize data on mount - FIXED: Use useEffect instead of useState
  useEffect(() => {
    fetchModules()
  }, [fetchModules])

  const createModule = async (formData: ModuleFormData) => {
    if (!formData.title || !formData.description) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      })
      return false
    }
    
    try {
      console.log('Creating module with data:', formData)
      await moduleService.createModule(formData)
      
      // Manually refresh the modules list
      await fetchModules()
      
      toast({
        title: "Module created",
        description: "New learn module has been added successfully",
      })
      return true
    } catch (error: any) {
      console.error('Error creating module:', error)
      toast({
        title: "Error",
        description: "Failed to create module",
        variant: "destructive",
      })
      return false
    }
  }

  const updateModule = async (moduleId: string, formData: ModuleFormData) => {
    try {
      console.log('Updating module:', moduleId, 'with data:', formData)
      await moduleService.updateModule(moduleId, formData)
      
      // Manually refresh the modules list to ensure UI updates
      await fetchModules()
      
      toast({
        title: "Module updated",
        description: "Learn module has been updated successfully",
      })
      return true
    } catch (error: any) {
      console.error('Error updating module:', error)
      toast({
        title: "Error",
        description: "Failed to update module",
        variant: "destructive",
      })
      return false
    }
  }

  const deleteModule = async (moduleId: string) => {
    try {
      console.log('Deleting module:', moduleId)
      await moduleService.deleteModule(moduleId)
      
      // Manually refresh the modules list
      await fetchModules()
      
      toast({
        title: "Module deleted",
        description: "Learn module has been removed successfully",
      })
    } catch (error: any) {
      console.error('Error deleting module:', error)
      toast({
        title: "Error",
        description: "Failed to delete module",
        variant: "destructive",
      })
    }
  }

  return {
    modules,
    loading,
    createModule,
    updateModule,
    deleteModule,
    refetch: fetchModules
  }
}

export type { LearnModule, ModuleFormData }
